import React, { useEffect, useState } from "react";
import {
  isPushSupported,
  isPushSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
  getNotificationPermission,
} from "../lib/pwa";
import { BellRing, BellOff, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PushButton({ compact = false }) {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const s = isPushSupported();
    setSupported(s);
    if (s) {
      isPushSubscribed().then(setSubscribed);
    }
  }, []);

  if (!supported) {
    return null;
  }

  const handleEnable = async () => {
    setBusy(true);
    try {
      await subscribeToPush(BACKEND_URL);
      setSubscribed(true);
      toast.success(
        "Notifiche push attivate! Riceverai avvisi anche con l'app chiusa.",
      );
    } catch (e) {
      const perm = getNotificationPermission();
      if (perm === "denied") {
        toast.error(
          "Permesso negato. Abilita le notifiche dalle impostazioni del browser/telefono.",
        );
      } else {
        toast.error(e.message || "Impossibile attivare le notifiche");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush(BACKEND_URL);
      setSubscribed(false);
      toast.success("Notifiche push disattivate.");
    } catch {
      toast.error("Errore nella disattivazione");
    } finally {
      setBusy(false);
    }
  };

  const common = compact
    ? "inline-flex items-center gap-2 rounded px-3 py-2 text-sm font-semibold transition-colors"
    : "inline-flex items-center gap-2 rounded px-4 py-2.5 text-sm font-semibold transition-colors shadow-sm";

  return (
    <>
      {subscribed ? (
        <button
          onClick={handleDisable}
          disabled={busy}
          data-testid="push-disable-button"
          className={`${common} bg-emerald-50 text-emerald-900 border border-emerald-300 hover:bg-emerald-100`}
          title="Disattiva le notifiche push"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <BellRing className="w-4 h-4" />
          )}
          {compact ? "Attive" : "Notifiche attive"}
        </button>
      ) : (
        <button
          onClick={handleEnable}
          disabled={busy}
          data-testid="push-enable-button"
          className={`${common} bg-blue-900 text-white hover:bg-blue-950`}
          title="Attiva le notifiche push"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <BellOff className="w-4 h-4" />
          )}
          {compact ? "Attiva notifiche" : "Attiva notifiche push"}
        </button>
      )}
      <button
        onClick={() => setShowInfo(true)}
        className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 transition-colors"
        title="Come funzionano le notifiche"
        data-testid="push-info-button"
      >
        <Info className="w-4 h-4" />
      </button>

      {showInfo && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4"
          onClick={() => setShowInfo(false)}
          data-testid="push-info-backdrop"
        >
          <div
            className="panel max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">
              Notifiche push - come funzionano
            </h3>
            <ul className="space-y-2 text-sm text-slate-700 list-disc pl-5">
              <li>
                Quando un prodotto scende sotto la soglia minima, il dispositivo
                riceve una notifica di sistema (anche con l'app chiusa).
              </li>
              <li>
                L'icona dell'app mostra un <b>badge numerico</b> con il numero
                di prodotti da controllare (Android, Windows, iOS 16.4+).
              </li>
              <li>
                Su <b>iPhone/iPad</b>: devi prima <b>installare l'app</b> con
                "Aggiungi a schermata Home" da Safari, poi aprirla e attivare
                le notifiche.
              </li>
              <li>
                Su <b>Android/Windows/Mac</b>: puoi attivare le notifiche anche
                senza installare, ma consigliamo di installare l'app per avere
                il badge sull'icona.
              </li>
            </ul>
            <button
              onClick={() => setShowInfo(false)}
              className="mt-5 w-full bg-slate-900 text-white rounded py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
              data-testid="push-info-close"
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </>
  );
}
